import { useMutation, useReactiveVar } from '@apollo/client';
import { get } from 'lodash';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import CustomNotification from '../../components/CustomNotification';
import { TEXT_CONSTANTS } from '../../constants/ui/text.constants';
import { sessionVar } from '../../graphql/cache';
import { UPDATE_USER } from '../../graphql/queries/user';
import { ROLES } from '../../constants/common.constants';

interface UserRoleType {
  id: string;
  name: string;
}

interface User {
  id: string;
  number: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  title: string;
  updatedAt: string;
  userRole?: UserRoleType;
}

interface SelectedUserParam extends User {
  managedUsers?: User[];
  manager: User | null;
}

interface SessionData {
  session?: {
    user: User;
  };
}

export const useUpsertUser = (): {
  upsertUser: (
    selectedUserParam: SelectedUserParam,
    managerUser: User | null,
    currentUserRole: string
  ) => Promise<{ success: boolean; error?: Error | undefined }>;
} => {
  const [upsertUserMutation] = useMutation(UPDATE_USER);
  const sessionData: SessionData = useReactiveVar(sessionVar);
  const currentUser = get(sessionData, 'session.user') as SelectedUserParam;

  const upsertUser = useCallback(
    async (
      selectedUserParam: SelectedUserParam,
      managerUser: User | null,
      currentUserRole: string
    ): Promise<{ success: boolean; error?: Error | undefined }> => {
      try {
        const record = {
          id: +selectedUserParam.id,
          roleId: selectedUserParam.userRole ? +selectedUserParam.userRole.id : null,
          managerId: managerUser ? +managerUser.id : null,
        };

        const response = await upsertUserMutation({ variables: record });

        if (response.errors) {
          // Extract the error message from the response
          const errorMessage = Array.isArray(response.errors[0].message)
            ? response.errors[0].message.join(', ')
            : response.errors[0].message;

          throw new Error(errorMessage);
        }

        const userHasSalesReps =
          currentUserRole === ROLES.SALES_MANAGER &&
          currentUser &&
          currentUser.managedUsers &&
          currentUser?.managedUsers?.length > 0;

        if (!userHasSalesReps) {
          const getSession = get(sessionData, 'getSessionData', () => null);
          getSession();
        }

        toast.success(({ toastProps }) => (
          <CustomNotification
            type={toastProps.type || 'success'}
            message={
              currentUserRole === ROLES.SALES_MANAGER
                ? `${selectedUserParam.fullName} ${TEXT_CONSTANTS.ASSIGN_TO_ME_SUCCESS_MESSAGE}`
                : `${selectedUserParam.fullName} has been assigned to ${managerUser?.fullName}.`
            }
          />
        ));

        return { success: true };
      } catch (error) {
        toast.error(({ toastProps }) => (
          <CustomNotification
            type={toastProps.type || 'error'}
            message={
              managerUser ? 'Failed to assign sales manager' : 'Failed to update user'
            }
          />
        ));
        return { success: false, error: error as Error };
      }
    },
    [upsertUserMutation, sessionData, currentUser]
  );

  return { upsertUser };
};
